import type {
  CSSProperties,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  Ref,
} from "react";
import { Children, cloneElement, forwardRef, isValidElement } from "react";

type AnyProps = Record<string, unknown>;

export type SlotProps = HTMLAttributes<HTMLElement> & { children: ReactNode };

function assignRef<T>(ref: Ref<T> | undefined, value: T | null): void {
  if (!ref) {
    return;
  }
  if (typeof ref === "function") {
    ref(value);
  } else {
    ref.current = value;
  }
}

function composeRefs<T>(
  forwardedRef: Ref<T> | undefined,
  childRef: Ref<T> | undefined,
): (value: T | null) => void {
  return (value) => {
    assignRef(forwardedRef, value);
    assignRef(childRef, value);
  };
}

function mergeProps(slotProps: AnyProps, childProps: AnyProps): AnyProps {
  const forwardedSlotProps = Object.fromEntries(
    Object.entries(slotProps).filter(([key]) => !key.startsWith("$")),
  );
  const merged = { ...forwardedSlotProps, ...childProps };

  for (const [key, childValue] of Object.entries(childProps)) {
    const slotValue = forwardedSlotProps[key];

    if (
      /^on[A-Z]/.test(key) &&
      typeof slotValue === "function" &&
      typeof childValue === "function"
    ) {
      // Always compose both (Radix Slot behaviour). Callers that care about
      // defaultPrevented should check it themselves — skipping the slot
      // handler breaks asChild + react-router Link (which preventDefaults).
      merged[key] = (...args: unknown[]) => {
        childValue(...args);
        slotValue(...args);
      };
    } else if (key === "className") {
      merged[key] = [slotValue, childValue].filter(Boolean).join(" ");
    } else if (key === "style") {
      merged[key] = {
        ...(slotValue as CSSProperties | undefined),
        ...(childValue as CSSProperties | undefined),
      };
    }
  }

  return merged;
}

export const Slot = forwardRef<HTMLElement, SlotProps>(
  (props, forwardedRef) => {
    const { children, ...slotProps } = props;
    const child = Children.only(children);

    if (!isValidElement(child)) {
      throw new Error("Slot requires a single React element child.");
    }

    const element = child as ReactElement<AnyProps>;
    const childRef = (element.props as AnyProps & { ref?: Ref<HTMLElement> })
      .ref;
    return cloneElement(element, {
      ...mergeProps(slotProps as AnyProps, element.props),
      ref: composeRefs(forwardedRef, childRef),
    });
  },
);

Slot.displayName = "Slot";
